/**
 * Fila „Note EA” de pe Amazon → Încasări: notele contabile ale unei luni din circuitul Amazon
 * (încasări, compensări, cardul Wise, diferența de curs), ca fișier de import în ExpertAccounts.
 * Vezi `amazon.note.service.ts` în API. Design: 2026-10-31-amazon-incasari-design.md (etapa 4).
 */
import { useEffect, useState } from 'react'
import { api } from '../../lib/api'
import { downloadXlsx } from '../../lib/downloadXlsx'
import { useAuthStore } from '../../stores/auth'
import { useConfirmare } from '../../components/ui/Confirmare'
import { Card } from '../../components/ui/Card'

interface Previzualizare {
  luna: string
  descarcatLa: string | null
  note: number
  sumar: { tip: string; eticheta: string; n: number; suma: number }[]
  excluse: { sursa: string; descriere: string; suma: number; motiv: string }[]
  control: {
    soldEur: number
    soldRon: number
    cursSfarsitLuna: number
    diferentaCurs: number
  } | null
  trimitere: {
    canal: 'fisier' | 'api' | null
    total: number
    trimise: number
    faraRaspuns: number
    respinse: number
    inCurs: boolean
    ultimaEroare: string | null
  }
}

const fmt = (n: number) =>
  n.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** Mesajul serverului, și când răspunsul a venit ca blob (descărcarea). */
async function mesaj(e: unknown, implicit: string): Promise<string> {
  const d = (e as { response?: { data?: unknown } })?.response?.data
  if (d instanceof Blob) {
    try {
      const j = JSON.parse(await d.text()) as { error?: { message?: string } }
      return j.error?.message ?? implicit
    } catch {
      return implicit
    }
  }
  const o = d as { error?: { message?: string } } | undefined
  return o?.error?.message ?? (e instanceof Error ? e.message : implicit)
}

/** Luna trecută, `YYYY-MM`: de regulă se face după ce vin rapoartele lunii. */
function lunaTrecuta(): string {
  const d = new Date()
  d.setUTCDate(1)
  d.setUTCMonth(d.getUTCMonth() - 1)
  return d.toISOString().slice(0, 7)
}

export function NoteEaAmazon() {
  const permisiuni = useAuthStore((s) => s.permissions)
  const admin = useAuthStore((s) => s.user?.role === 'admin')
  const [luna, setLuna] = useState(lunaTrecuta())
  const [p, setP] = useState<Previzualizare | null>(null)
  const [lucreaza, setLucreaza] = useState<'verific' | 'descarc' | 'trimit' | null>(null)
  const [eroare, setEroare] = useState<string | null>(null)
  const { confirma } = useConfirmare()

  // Cat timp se trimite in EA, starea se reciteste la 3 secunde.
  const trimiteAcum = p?.trimitere?.inCurs ?? false
  useEffect(() => {
    if (!trimiteAcum) return
    const t = setInterval(async () => {
      try {
        setP((await api.get(`/amazon/note-ea?luna=${luna}`)).data)
      } catch {
        /* reincearca la urmatorul pas */
      }
    }, 3000)
    return () => clearInterval(t)
  }, [trimiteAcum, luna])

  if (!permisiuni.includes('emag:write')) {
    return (
      <p className="text-sm text-slate-500">Notele pentru EA le pregătesc doar administratorii.</p>
    )
  }

  const verifica = async (l = luna) => {
    setLucreaza('verific')
    setEroare(null)
    try {
      setP((await api.get(`/amazon/note-ea?luna=${l}`, { timeout: 4 * 60_000 })).data)
    } catch (e) {
      setP(null)
      setEroare(await mesaj(e, 'Nu am putut pregăti notele.'))
    } finally {
      setLucreaza(null)
    }
  }

  const descarca = async () => {
    if (
      p?.descarcatLa &&
      !(await confirma({
        titlu: 'Fișierul acestei luni a mai fost descărcat',
        mesaj: `Prima dată pe ${new Date(p.descarcatLa).toLocaleString('ro-RO')}. Dacă l-ai importat deja în EA, un al doilea import dublează notele. Descărcat din nou, înlocuiește notele salvate ale lunii.`,
        buton: 'Descarcă totuși',
        pericol: true,
      }))
    ) {
      return
    }
    setLucreaza('descarc')
    setEroare(null)
    try {
      await downloadXlsx(`/amazon/note-ea/fisier?luna=${luna}`, `EA note Amazon ${luna}.xlsx`)
      await verifica()
    } catch (e) {
      setEroare(await mesaj(e, 'Fișierul nu s-a putut face.'))
    } finally {
      setLucreaza(null)
    }
  }

  const trimite = async () => {
    if (
      !(await confirma({
        titlu: `Trimit notele din ${luna} în EA?`,
        mesaj: `Intră direct în contabilitate, validate (${p?.note ?? 0} note). Luna nu se mai poate descărca apoi ca fișier.`,
        buton: 'Trimite în EA',
        pericol: true,
      }))
    ) {
      return
    }
    setLucreaza('trimit')
    setEroare(null)
    try {
      await api.post('/amazon/note-ea/trimite', { luna }, { timeout: 4 * 60_000 })
      await verifica()
    } catch (e) {
      setEroare(await mesaj(e, 'Trimiterea nu a pornit.'))
    } finally {
      setLucreaza(null)
    }
  }

  const tr = p?.trimitere
  const prinApi = tr?.canal === 'api'

  const butonCls =
    'whitespace-nowrap rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50'

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">
        Notele contabile ale unei luni, gata de importat în EA la „Note contabile noi”: încasarea
        facturilor de vânzare, compensarea facturilor Amazon din sold, plățile de pe cardul Wise și
        diferența de curs lunară. Înainte de fișier, CRM-ul întreabă EA ce documente mai sunt
        deschise, ca nimic să nu se dubleze.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <input
          type="month"
          className="rounded-md border px-2 py-1.5 text-sm"
          value={luna}
          onChange={(e) => {
            setLuna(e.target.value)
            setP(null)
          }}
        />
        <button className={butonCls} disabled={!!lucreaza || !luna} onClick={() => verifica()}>
          {lucreaza === 'verific' ? 'Întreb EA…' : 'Pregătește notele'}
        </button>
        {admin && p && p.note > 0 && !prinApi && (
          <>
            <button className={butonCls} disabled={!!lucreaza} onClick={trimite}>
              {lucreaza === 'trimit' ? 'Pornesc trimiterea…' : 'Trimite în EA'}
            </button>
            <button
              className="whitespace-nowrap rounded-md border px-4 py-2 text-sm hover:bg-slate-50 disabled:opacity-50"
              disabled={!!lucreaza || tr?.canal === 'fisier'}
              onClick={descarca}
            >
              {lucreaza === 'descarc' ? 'Se face fișierul…' : 'Descarcă fișierul'}
            </button>
          </>
        )}
        {admin && prinApi && tr && !tr.inCurs && tr.trimise < tr.total && (
          <button className={butonCls} disabled={!!lucreaza} onClick={trimite}>
            Reia trimiterea
          </button>
        )}
        {p?.descarcatLa && (
          <span className="rounded bg-amber-50 px-2 py-1 text-xs text-amber-800">
            descărcat pe {new Date(p.descarcatLa).toLocaleString('ro-RO')}
          </span>
        )}
      </div>

      {eroare && (
        <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{eroare}</div>
      )}

      {prinApi && tr && (
        <Card className="p-4 text-sm">
          <div className="font-medium">
            {tr.inCurs
              ? 'Se trimit în EA…'
              : tr.trimise === tr.total
                ? 'Trimise în EA'
                : 'Trimitere oprită'}
          </div>
          <div className="mt-1 text-slate-600">
            {tr.trimise} din {tr.total} note trimise
            {tr.faraRaspuns > 0 &&
              ` · ${tr.faraRaspuns} fără răspuns de la EA (pot să fi intrat — de verificat în Registrul Jurnal)`}
          </div>
          {tr.respinse > 0 && tr.ultimaEroare && (
            <div className="mt-2 rounded bg-red-50 px-2 py-1 text-red-700">
              EA a respins o notă și trimiterea s-a oprit: {tr.ultimaEroare}
            </div>
          )}
        </Card>
      )}

      {p && !prinApi && (
        <>
          <Card className="overflow-x-auto p-0">
            <table className="w-full text-sm">
              <thead className="border-b bg-slate-50 text-left text-slate-500">
                <tr>
                  <th className="px-3 py-2">Note</th>
                  <th className="px-3 py-2 text-right">Câte</th>
                  <th className="px-3 py-2 text-right">Lei</th>
                </tr>
              </thead>
              <tbody>
                {p.sumar.length === 0 && (
                  <tr>
                    <td colSpan={3} className="px-3 py-6 text-center text-slate-400">
                      Nimic de înregistrat în luna asta.
                    </td>
                  </tr>
                )}
                {p.sumar.map((s) => (
                  <tr key={s.tip} className="border-b last:border-0">
                    <td className="px-3 py-2">{s.eticheta}</td>
                    <td className="px-3 py-2 text-right">{s.n}</td>
                    <td className="px-3 py-2 text-right">{fmt(s.suma)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          {p.control && (
            <Card className="p-4">
              <div className="flex flex-wrap gap-6 text-sm">
                <div>
                  <div className="text-slate-500">Sold 5125.Amazon la sfârșitul lunii</div>
                  <div className="text-lg font-semibold">
                    {fmt(p.control.soldEur)} EUR = {fmt(p.control.soldRon)} lei
                  </div>
                </div>
                <div>
                  <div className="text-slate-500">Curs BNR la sfârșitul lunii</div>
                  <div className="text-lg font-semibold">{p.control.cursSfarsitLuna}</div>
                </div>
                <div>
                  <div className="text-slate-500">Diferență de curs</div>
                  <div className="text-lg font-semibold">{fmt(p.control.diferentaCurs)} lei</div>
                </div>
              </div>
            </Card>
          )}

          {p.excluse.length > 0 && (
            <Card className="overflow-x-auto p-0">
              <div className="border-b bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900">
                Rămân pe dinafară ({p.excluse.length})
              </div>
              <table className="w-full text-xs">
                <tbody>
                  {p.excluse.map((e) => (
                    <tr key={e.sursa} className="border-b last:border-0">
                      <td className="px-3 py-1.5">{e.descriere}</td>
                      <td className="px-3 py-1.5 text-right">{fmt(e.suma)}</td>
                      <td className="px-3 py-1.5 text-slate-600">{e.motiv}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </>
      )}
    </div>
  )
}
